import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { variation } from 'ember-launch-darkly';
import { eq, and, or } from 'ember-truth-helpers';
import can from 'ember-can/helpers/can';
import {
  HdsTable,
  HdsLinkInline,
  HdsBadge,
  HdsTextBody,
  HdsTooltipButton,
  HdsIcon,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';
import { Time, Flex } from 'core/components';
import { boxPadding } from 'core/modifiers';
import { v2ResourceDisplayName } from 'core/helpers';
import DeleteGroup from 'hcp/components/iam/groups/modals/delete-group';

/**
 *
 * `IamGroupsListTable` shows summary details of a group, and it's members.
 *
 *
 * ```
 * <Iam::Groups::ListTable
 *  @groups={{foo}}
 *  @groupsPermissions={{foo}}
 *  @scimEnabled={{false}}
 * />
 * ```
 *
 * @class IamGroupsListTable
 *
 */
export default class IamGroupsListTable extends Component {
  /**
   * @argument groups
   * @type {Object}
   * @required
   */
  /**
   * @argument groupsPermissions
   * @type {Object}
   * @required
   */
  /**
   * @argument scimEnabled
   * @type {Boolean}
   * @required
   */

  @service abilities;

  @tracked group;
  @tracked isActive = false;

  openDeleteModal = (group) => {
    this.isActive = true;
    this.group = group;
  };

  get displayScimProvisioningColumn() {
    const { ssoEnabled } = this.args;
    return ssoEnabled && variation('hcp-identity-scim-enabled');
  }

  get shouldShowActionsHeader() {
    const { groups } = this.args;
    return groups.some((group) => this.canUpdateGroup(group));
  }

  closeDeleteModal = () => {
    this.group = null;
    this.isActive = false;
  };

  canListGroupMembers = (group) => {
    const { groupsPermissions } = this.args;

    if (!groupsPermissions || !group.resourceName) {
      return false;
    }
    const permissions =
      groupsPermissions?.[group.resourceName]?.allowedPermissions;

    return this.abilities.can('list-members groups', group, {
      groupLevelPermissions: permissions,
    });
  };

  canUpdateGroup = (group) => {
    const { groupsPermissions } = this.args;

    if (!groupsPermissions || !group.resourceName) {
      return false;
    }
    const permissions =
      groupsPermissions?.[group.resourceName]?.allowedPermissions;

    return this.abilities.can('update group', group, {
      groupLevelPermissions: permissions,
    });
  };

  <template>
    <HdsTable
      @caption={{t "components.page.access-control.groups.list.title"}}
      @valign="middle"
      data-test-groups-list-table
    >
      <:head as |H|>
        <H.Tr>
          <H.Th>
            {{t
              "components.page.access-control.groups.list.table.header.group-name"
            }}
          </H.Th>
          <H.Th>
            {{t
              "components.page.access-control.groups.list.table.header.description"
            }}
          </H.Th>
          <H.Th>
            {{t
              "components.page.access-control.groups.list.table.header.members"
            }}
          </H.Th>
          {{#if this.displayScimProvisioningColumn}}
            <H.Th>
              {{t
                "components.page.access-control.groups.list.table.header.scim-provisioning"
              }}
            </H.Th>
          {{/if}}
          <H.Th @width="10em">
            {{t
              "components.page.access-control.groups.list.table.header.date-added"
            }}
          </H.Th>
          {{#if this.shouldShowActionsHeader}}
            <H.Th>
              {{t
                "components.page.access-control.groups.list.table.header.actions"
              }}
            </H.Th>
          {{/if}}
        </H.Tr>
      </:head>
      <:body as |B|>
        {{#each @groups key="id" as |group|}}
          {{#let
            (and @scimEnabled (eq group.scimSynchronized true))
            as |scimManaged|
          }}
            <B.Tr data-test-group-item-row>
              <B.Td data-test-group-item-cell data-test-group-name>
                <Flex @alignItems="center" @gap="2xs">
                  {{#if (this.canListGroupMembers group)}}
                    <HdsLinkInline
                      @color="secondary"
                      @route="cloud.access-control.groups.detail"
                      @model={{group.resourceName}}
                      class="hds-font-weight-medium"
                      data-test-group-name-hyperlink={{group.resourceName}}
                    >
                      {{group.displayName}}
                    </HdsLinkInline>
                  {{else}}
                    <span data-test-group-name-text>{{group.displayName}}</span>
                  {{/if}}
                </Flex>
              </B.Td>
              <B.Td data-test-group-item-cell data-test-group-description>
                {{group.description}}
              </B.Td>
              <B.Td data-test-group-item-cell data-test-group-member-count>
                {{group.memberCount}}
              </B.Td>
              {{#if this.displayScimProvisioningColumn}}
                <B.Td data-test-group-item-scim-provisioning>
                  <Flex @alignItems="center" @direction="row" @gap="md">
                    {{#let
                      (and @scimEnabled (eq group.scimSynchronized true))
                      (and @scimEnabled (eq group.scimSynchronized false))
                      (eq @scimEnabled false)
                      as |syncing createdInHcp notEnabled|
                    }}
                      {{#if (or syncing notEnabled)}}

                        <HdsBadge
                          @text={{if
                            syncing
                            (t
                              "components.page.access-control.groups.list.scim-provisioning.syncing"
                            )
                            (t
                              "components.page.access-control.groups.list.scim-provisioning.not-enabled"
                            )
                          }}
                          @type="outlined"
                          @icon={{if syncing "check" "x"}}
                          @color={{if syncing "success"}}
                          data-test-group-item-scim-provisioning-badge
                        />
                      {{else}}
                        <Flex @alignItems="center" @direction="row" @gap="sm">
                          <HdsTextBody
                            @color="faint"
                            @size="200"
                            @weight="regular"
                            data-test-group-item-scim-provisioning-created-in-hcp
                          >
                            {{t
                              "components.page.access-control.groups.list.scim-provisioning.created-in-hcp"
                            }}
                          </HdsTextBody>
                          <HdsTooltipButton
                            @text={{t
                              "components.page.access-control.groups.list.scim-provisioning.created-in-hcp-tooltip"
                            }}
                            @placement="bottom"
                            aria-label={{t
                              "components.page.access-control.groups.list.scim-provisioning.created-in-hcp-tooltip"
                            }}
                            data-test-group-item-scim-provisioning-created-in-hcp-tooltip
                          >
                            <HdsIcon
                              @name="alert-circle"
                              @color="var(--token-color-foreground-faint)"
                              @isInline={{true}}
                            />
                          </HdsTooltipButton>
                        </Flex>
                      {{/if}}
                    {{/let}}
                  </Flex>
                </B.Td>
              {{/if}}
              <B.Td data-test-group-item-cell data-test-group-created-at>
                <Time @date={{group.createdAt}} @display="friendly-only" />
              </B.Td>
              {{#if (this.canUpdateGroup group)}}
                <B.Td data-test-group-item-cell>
                  <Flex @justifyContent="end">
                    <HdsDropdown data-test-more-menu as |dd|>
                      <dd.ToggleIcon
                        @icon="more-horizontal"
                        @text={{t "components.form.more"}}
                        @hasChevron={{false}}
                        data-test-more-menu-trigger
                        data-test-group-item-menu
                      />
                      <dd.Interactive
                        @icon="edit"
                        @model={{group.resourceId}}
                        @route="cloud.access-control.role-assignments.detail.edit"
                        @text={{t
                          "components.page.access-control.groups.list.actions.edit-role-assignments"
                        }}
                        data-test-edit-assignment-button
                      />
                      {{#if scimManaged}}
                        <dd.Separator />
                        <dd.Generic {{boxPadding "xs sm xs md"}}>
                          <HdsTextBody
                            @color="primary"
                            @size="200"
                            data-test-groups-scim-manage
                          >
                            {{t
                              "components.page.access-control.groups.list.actions.manage-scim-group"
                            }}
                          </HdsTextBody>
                        </dd.Generic>
                      {{else}}
                        <dd.Interactive
                          @icon="edit"
                          @route="cloud.access-control.groups.detail.edit"
                          @model={{group.resourceName}}
                          @text={{t
                            "components.page.access-control.groups.list.actions.edit"
                          }}
                          data-test-groups-member-edit
                        />
                        {{#if (can "delete groups")}}
                          <dd.Separator />
                          <dd.Interactive
                            @color="critical"
                            @icon="trash"
                            @text={{t
                              "components.page.access-control.groups.list.actions.delete"
                            }}
                            {{on "click" (fn this.openDeleteModal group)}}
                            data-test-groups-delete-button
                          />
                        {{/if}}
                      {{/if}}
                    </HdsDropdown>
                  </Flex>
                </B.Td>
              {{else}}
                {{#if this.shouldShowActionsHeader}}
                  <B.Td data-test-group-item-cell />
                {{/if}}
              {{/if}}
            </B.Tr>
          {{/let}}
        {{/each}}
      </:body>
    </HdsTable>

    <DeleteGroup
      @isActive={{this.isActive}}
      @closeModal={{this.closeDeleteModal}}
      @group={{this.group}}
      @groupName={{v2ResourceDisplayName this.group.resourceName}}
    />
  </template>
}
